<template>
  <div
    class="accordion-panel"
    :class="{
      expanded: expanded,
      disabled: disabled,
      completed: useOmwIcon,
    }"
  >
    <div
      class="accordion-header"
      :class="{ disabled: disabled }"
      @click="$emit('toggle', 'willStorage')"
    >
      <div class="header-main">
        <div class="header-icon">
          <img
            v-if="useOmwIcon"
            :src="whitelabel.images.omwIcon"
            alt=""
            width="24"
            height="24"
          />
          <v-icon v-else :color="headerIconColor" size="24">
            {{ headerIcon }}
          </v-icon>
        </div>

        <div class="header-title">
          <h3 :style="{ color: headerTitleColor }">{{ headerTitle }}</h3>
        </div>

        <div class="header-chevron">
          <v-icon color="#666" size="20">
            {{ expanded ? icons.mdiChevronUp : icons.mdiChevronDown }}
          </v-icon>
        </div>
      </div>
    </div>

    <div class="expanded-container" :class="{ expanded: expanded }">
      <div class="status-container">
        <p class="status-text">
          Status:
          <span
            class="status-value"
            :style="{
              color: statusColor,
            }"
          >
            {{ displayStorageStatus }}
          </span>
        </p>
      </div>

      <div class="accordion-content">
        <div class="content-wrapper">
          <div class="section-content">
            <div class="content-margins" v-if="isStoredButOutdated">
              <p>
                <strong>This version is still your validated will.</strong>
                Only upon upload and validation of your new will, will this
                version of your will be replaced.
              </p>
              <div class="action-buttons">
                <GradientButton
                  v-if="previouslyStoredUpload"
                  :title="'View validated and vaulted will'"
                  @gradientButtonClicked="
                    $emit('viewUploadedWill', previouslyStoredUpload.id)
                  "
                />
              </div>
            </div>
            <div class="content-margins" v-else-if="isStored">
              <p class="mb-1">
                <strong>Version:</strong> {{ latestVersion.version }}
                &nbsp;&nbsp;|&nbsp;&nbsp;
                <strong>Created</strong>
                {{ formattedVersionDate }}
              </p>
              <div class="action-buttons">
                <GradientButton
                  v-if="latestUpload"
                  :title="'View your validated will'"
                  @gradientButtonClicked="
                    $emit('viewUploadedWill', latestUpload.id)
                  "
                />
              </div>
            </div>
            <div class="content-margins" v-else>
              <p
                v-if="
                  willStorageStatus === 'Not stored' ||
                  willStorageStatus === 'Not Scheduled'
                "
              >
                Storing your will in the
                <strong>Old Mutual Wills Vault</strong> ensures that we can
                quickly and effectively start the executor process
              </p>
              <div class="action-buttons">
                <GradientButton
                  v-if="determineWillBooking"
                  :title="'Schedule collection'"
                  @gradientButtonClicked="$emit('scheduleCollection')"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import {
    mdiChevronDown,
    mdiChevronUp,
    mdiCheckCircle,
    mdiAlertCircleOutline,
  } from '@mdi/js';
  import {
    latestWillVersion,
    latestUploadedWill,
  } from '../../../constants/js/will-verification';

  export default {
    name: 'SendWillForStorage',
    props: {
      expanded: {
        type: Boolean,
        default: false,
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      completed: {
        type: Boolean,
        default: false,
      },
      willStorageStatus: {
        type: String,
        required: true,
      },
      willCollectionBooked: {
        type: Boolean,
        default: false,
      },
      willBookingDetails: {
        type: String,
        required: true,
      },
      testator: {
        type: Object,
        required: true,
      },
    },
    data() {
      return {
        icons: {
          mdiChevronDown: mdiChevronDown,
          mdiChevronUp: mdiChevronUp,
          mdiCheckCircle: mdiCheckCircle,
          mdiAlertCircleOutline: mdiAlertCircleOutline,
        },
      };
    },
    computed: {
      latestVersion() {
        return latestWillVersion(this.testator?.willVersions || []);
      },
      latestUpload() {
        return latestUploadedWill(this.testator?.willVersions || []);
      },
      isStored() {
        return !!this.testator.dateStored;
      },
      previouslyStoredVersion() {
        const versions = this.testator?.willVersions || [];
        return versions.find(v => v.dateStored);
      },
      previouslyStoredUpload() {
        if (!this.previouslyStoredVersion) return null;
        return latestUploadedWill([this.previouslyStoredVersion]);
      },
      isStoredButOutdated() {
        return (
          !this.isStored &&
          !!this.previouslyStoredVersion &&
          this.latestVersion?.status !== 'VALID'
        );
      },
      headerTitle() {
        if (this.isStoredButOutdated) {
          const storedVersion = this.previouslyStoredVersion;
          const date = storedVersion?.dateStored
            ? new Date(storedVersion.dateStored).toISOString().split('T')[0]
            : '';
          return `WILL VALIDATED/VAULTED - ${date}`;
        }
        if (this.isStored) {
          const date = this.testator.dateStored
            ? new Date(this.testator.dateStored).toISOString().split('T')[0]
            : '';
          return `WILL VALIDATED AND VAULTED - ${date}`;
        }
        return 'SEND WILL FOR STORAGE';
      },
      headerTitleColor() {
        if (this.isStoredButOutdated) return undefined;
        if (this.isStored) return this.whitelabel.colors.leftPanelGreen;
        return undefined;
      },
      useOmwIcon() {
        return this.isStored && !this.isStoredButOutdated;
      },
      headerIcon() {
        if (this.isStoredButOutdated) return this.icons.mdiAlertCircleOutline;
        return this.icons.mdiCheckCircle;
      },
      headerIconColor() {
        if (this.isStoredButOutdated) return '#FF6F61';
        if (this.completed) return this.whitelabel.colors.leftPanelGreen;
        return this.whitelabel.colors.leftPanelIconGrey;
      },
      formattedVersionDate() {
        if (!this.latestVersion?.createdAt) return '';
        return new Date(this.latestVersion.createdAt)
          .toISOString()
          .split('T')[0];
      },
      determineWillBooking() {
        if (this.collectionCancelled) {
          return true;
        }

        return (
          this.determineWillValidity &&
          !this.testator.willCollectionBooked &&
          !this.activeWillCollectionBooking
        );
      },
      displayStorageStatus() {
        if (this.isStoredButOutdated) {
          const storedVersion = this.previouslyStoredVersion;
          const date = storedVersion?.dateStored
            ? new Date(storedVersion.dateStored).toISOString().split('T')[0]
            : '';
          return `Stored in Vault - ${date}`;
        }
        return this.willStorageStatus;
      },
      statusColor() {
        if (this.isStoredButOutdated) {
          return this.whitelabel.colors.leftPanelGreen;
        }
        if (
          this.willStorageStatus.startsWith('Stored') ||
          this.willStorageStatus.startsWith('Collection booked') ||
          this.willStorageStatus.startsWith('Collected') ||
          this.willStorageStatus.startsWith('Delivered')
        ) {
          return this.whitelabel.colors.leftPanelGreen;
        }
        return this.whitelabel.colors.leftPanelOrange;
      },
      determineWillValidity() {
        return this.latestVersion?.status === 'VALID';
      },
      activeWillCollectionBooking() {
        return this.testator.activeWillCollectionBooking || null;
      },
      collectionCancelled() {
        // Use latestWillCollectionBooking (the most recent booking regardless of
        // status) rather than activeWillCollectionBooking. A booking is only
        // "active" while its collection date is in the future, so a cancelled
        // (or past-dated) booking has activeWillCollectionBooking === null and the
        // cancellation could never be detected — leaving the user unable to
        // re-schedule after a cancellation.
        if (!this.testator.latestWillCollectionBooking) {
          return false;
        }

        return (
          this.testator.latestWillCollectionBooking.collectionStatus ===
            'cancelled' &&
          (this.testator.latestWillCollectionBooking.willVersionId ===
            this.latestVersion?.id ||
            this.determineWillValidity)
        );
      },
    },
  };
</script>
